'use client';

import { type ReactNode } from 'react';
import type { AboutComponent, TransformedAboutData } from '@/types/aboutus';
import HomeHeroSlider from '@/components/sections/Home/HomeHeroSlider';
import HistoryTimeline from '@/components/sections/Home/HistoryTimeline';
import AboutPreFooterSection from '../components/AboutPreFooterSection';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import SalesCallbackProvider from '@/components/sales-callback/SalesCallbackProvider';
import SalesCallbackSection from '@/components/sales-callback/SalesCallbackSection';
import AboutMissionVisionScroll from '../components/AboutMissionVisionScroll';
import AboutFivePillarsTrust from '../components/AboutFivePillarsTrust';
import AboutSafetyQualityCommitments from '../components/AboutSafetyQualityCommitments';
import AboutStoryStatsSection from '../components/AboutStoryStatsSection';
import { stripHtml } from '@/lib/homepage-mappers';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import type { ContactForm } from '@/types/contact-forms';

interface AboutClientProps {
  locale: string;
  aboutData: TransformedAboutData | null;
  emailContactForm?: ContactForm | null;
  /**
   * Streaming slot for the FAQ + enquiry block at the page bottom. The
   * lead-forms Strapi fetch happens in an async Server Component over
   * in `page.tsx` and is wrapped in `<Suspense>`, so the above-fold
   * (hero, intro, stats) paints without waiting on it.
   */
  faqSlot?: ReactNode;
}

function renderAboutComponent(component: AboutComponent, locale: string, faqSlot?: ReactNode) {
  switch (component.__component) {
    case 'common.spacer-top':
    case 'common.spacer-full':
    case 'common.spacer-bottom':
      return <CommonSpacer key={component.id} zone={component} />;

    case 'about-us.stats-with-image-grid':
      return (
        <AboutStoryStatsSection
          key={component.id}
          zone={component}
          content={component.description}
        />
      );

    case 'modules.timeline':
      return <HistoryTimeline key={component.id} theme="light" timelineComponent={component} />;

    case 'modules.text-image':
      return (
        <div key={component.id} className="relative z-40">
          <AboutMissionVisionScroll
            heading={component.title}
            description={component.description || undefined}
            imageSrc={
              component.media?.url
                ? (resolveStrapiMediaUrl(component.media.url) ?? undefined)
                : undefined
            }
          />
        </div>
      );

    case 'about-us.pillars-section':
      return (
        <AboutFivePillarsTrust
          key={component.id}
          heading={component.title}
          subheading={component.description ? stripHtml(component.description) : undefined}
          pillars={
            component.dataWrapper?.length
              ? component.dataWrapper.map((pillar, idx) => ({
                  num: String(idx + 1).padStart(2, '0'),
                  title: pillar.heading,
                  description: pillar.description,
                }))
              : undefined
          }
        />
      );

    case 'about-us.safety-quality':
      return <AboutSafetyQualityCommitments key={component.id} zone={component} />;

    case 'common.pre-footer':
      return <AboutPreFooterSection key={component.id} zone={component} locale={locale} />;

    case 'modules.faqwith-form':
      return faqSlot ? <div key={component.id}>{faqSlot}</div> : null;

    default:
      return null;
  }
}

export default function AboutClient({
  locale,
  aboutData,
  emailContactForm,
  faqSlot,
}: AboutClientProps) {
  if (!aboutData) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">About Us</h1>
          <p className="text-xl text-gray-600">
            Sorry, we couldn&apos;t load the about page content. Please try again later.
          </p>
        </div>
      </main>
    );
  }

  const heroSlides = aboutData.bannerSlides ?? [];
  const components = aboutData.components ?? [];

  return (
    <SalesCallbackProvider variant="corporate" formConfig={emailContactForm}>
      <SalesCallbackSection mode="page">
        <main className="min-h-screen w-full max-lg:overflow-x-visible overflow-x-clip">
          <section
            className={`${heroSlides.length === 0 ? 'hidden' : ''} relative bg-black text-white`}
          >
            <HomeHeroSlider showSecondaryCta={false} slides={heroSlides as never} locale={locale} />
          </section>

          {components.map((component) => renderAboutComponent(component, locale, faqSlot))}
        </main>
      </SalesCallbackSection>
    </SalesCallbackProvider>
  );
}
